import { useQuery, type UseQueryResult } from "@tanstack/react-query"

import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"

import type {
  AssignmentAction,
  AssignmentEvent,
  ReviewStage,
} from "../data/review-data"

interface AssignmentEventOutWire {
  id: number
  action: string
  actor_id: number | null
  from_user_id: number | null
  to_user_id: number | null
  to_stage: string | null
  note: string | null
  created_at: string
}

const ACTIONS: readonly AssignmentAction[] = [
  "assign",
  "reassign",
  "throwback",
  "advance_stage",
  "skip_secondary",
  "archive",
  "unarchive",
]

function toAction(raw: string): AssignmentAction {
  return (ACTIONS as readonly string[]).includes(raw)
    ? (raw as AssignmentAction)
    : "assign"
}

function toStage(raw: string | null): ReviewStage | null {
  if (raw === "initial" || raw === "secondary" || raw === "done") return raw
  return null
}

export type NameResolver = (userId: number | null) => string | null

function toEvent(w: AssignmentEventOutWire, resolveName: NameResolver): AssignmentEvent {
  return {
    action: toAction(w.action),
    at: w.created_at,
    actor: resolveName(w.actor_id),
    fromUser: resolveName(w.from_user_id),
    toUser: resolveName(w.to_user_id),
    toStage: toStage(w.to_stage),
    note: w.note,
  }
}

async function fetchHistory(
  reviewId: string,
  signal: AbortSignal,
): Promise<AssignmentEventOutWire[]> {
  return http.get<AssignmentEventOutWire[]>(
    `/api/v1/reviews/${encodeURIComponent(reviewId)}/assignment-events`,
    { signal },
  )
}

export function useReviewHistoryQuery(
  reviewId: string | null,
  resolveName: NameResolver,
): UseQueryResult<AssignmentEvent[]> {
  return useQuery({
    queryKey: reviewId != null ? qk.reviews.history(reviewId) : ["reviews", "history", "none"],
    queryFn: async ({ signal }) => {
      const wire = await fetchHistory(reviewId as string, signal)
      return wire.map((w) => toEvent(w, resolveName))
    },
    enabled: reviewId != null,
  })
}
